import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronRight } from 'lucide-react';
import { getContentIndex, Article } from '../content/index';
import Navbar from '../components/Navbar';
import '../i18n';

interface LoadedArticle extends Article {
  content: string;
}

interface StatsData {
  date: string;
  totalRequests: number;
  uniqueIps: number;
  uniqueIpsNoBot: number;
}

// 动态导入所有 markdown 文件
const articleFiles = import.meta.glob('../content/**/*.md', { as: 'raw' });

const Home: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [articles, setArticles] = useState<LoadedArticle[]>([]);
  const [activeArticle, setActiveArticle] = useState<string>('');
  const [contentIndex, setContentIndex] = useState(getContentIndex(i18n.language));
  const [stats, setStats] = useState<StatsData | null>(null);

  // 🔥 获取今日统计数据
  useEffect(() => {
    const fetchStats = async () => {
      try {
        console.log('🔍 [Home] 正在获取统计数据...');
        const response = await fetch('/api/stats/today');
        console.log('🔍 [Home] API 响应状态:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('🔍 [Home] 获取到统计数据:', data);
          setStats(data);
        } else {
          console.error('🔍 [Home] API 请求失败:', response.statusText);
        }
      } catch (error) {
        console.error('🔍 [Home] 获取统计数据失败:', error);
      }
    };
    
    fetchStats();
    // 每5分钟刷新一次
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 当语言变化时更新内容
  useEffect(() => {
    const newContentIndex = getContentIndex(i18n.language);
    setContentIndex(newContentIndex);
    
    const loadArticles = async () => {
      const loaded: LoadedArticle[] = [];
      
      for (const article of newContentIndex.articles) {
        const path = `../content/${i18n.language}/${article.file}`;
        if (articleFiles[path]) {
          const content = await articleFiles[path]();
          loaded.push({ ...article, content });
        }
      }
      
      loaded.sort((a, b) => a.order - b.order);
      setArticles(loaded);
      if (loaded.length > 0) {
        setActiveArticle(loaded[0].id);
      }
    };
    
    loadArticles();
  }, [i18n.language]);

  const scrollToArticle = (id: string) => {
    setActiveArticle(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 自定义 Markdown 组件渲染 - 简洁配色
  const markdownComponents: Components = {
    code: ({ inline, className, children, ...props }: any) => {
      if (inline) {
        return (
          <code
            className="px-1.5 py-0.5 mx-0.5 rounded bg-slate-100 text-indigo-600 font-mono text-sm border border-indigo-100"
            {...props}
          >
            {children}
          </code>
        );
      }
      return (
        <pre
          className={`block px-4 py-3 my-4 rounded-lg bg-slate-900 text-slate-100 font-mono text-sm overflow-auto border border-slate-700 max-h-60 ${className || ''}`}
          {...props}
        >
          <code>{children}</code>
        </pre>
      );
    },
    blockquote: ({ children, ...props }: any) => (
      <blockquote
        className="border-l-4 border-indigo-300 bg-slate-50 pl-4 pr-4 py-3 my-6 italic text-slate-600 rounded-r"
        {...props}
      >
        {children}
      </blockquote>
    ),
    ul: ({ children, ...props }: any) => (
      <ul className="list-disc list-inside my-4 space-y-2 text-slate-700" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }: any) => (
      <ol className="list-decimal list-inside my-4 space-y-2 text-slate-700" {...props}>
        {children}
      </ol>
    ),
    h1: ({ children, ...props }: any) => (
      <h1 className="text-3xl font-bold mt-16 mb-8 pb-4 border-b-2 border-slate-200 text-slate-800" {...props}>
        {children}
      </h1>
    ),
    h2: ({ children, ...props }: any) => (
      <h2 className="text-2xl font-bold mt-12 mb-6 text-slate-800 flex items-center gap-2" {...props}>
        <span className="w-1 h-6 bg-indigo-500 rounded-full"></span>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }: any) => (
      <h3 className="text-xl font-semibold mt-8 mb-4 text-slate-800" {...props}>
        {children}
      </h3>
    ),
    p: ({ children, ...props }: any) => (
      <p className="mb-4 text-slate-600 leading-relaxed" {...props}>
        {children}
      </p>
    ),
    strong: ({ children, ...props }: any) => (
      <strong className="font-semibold text-slate-800" {...props}>
        {children}
      </strong>
    ),
    em: ({ children, ...props }: any) => (
      <em className="italic text-slate-500" {...props}>
        {children}
      </em>
    ),
    a: ({ children, ...props }: any) => (
      <a className="text-indigo-600 underline underline-offset-4 hover:text-indigo-700 transition-colors" {...props}>
        {children}
      </a>
    ),
    table: ({ children, ...props }: any) => (
      <table className="w-full my-6 border-collapse border border-slate-200 rounded-lg overflow-hidden" {...props}>
        {children}
      </table>
    ),
    th: ({ children, ...props }: any) => (
      <th className="border-b-2 border-slate-300 px-4 py-2 text-left font-semibold text-slate-800 bg-slate-50" {...props}>
        {children}
      </th>
    ),
    td: ({ children, ...props }: any) => (
      <td className="border-b border-slate-100 px-4 py-2 text-slate-600" {...props}>
        {children}
      </td>
    ),
    hr: ({ ...props }: any) => (
      <hr className="my-8 border-t-2 border-slate-200" {...props} />
    ),
  };

  return (
    <div className="min-h-screen bg-white text-slate-800">
      {/* Navbar - 共享组件 */}
      <Navbar />

      {/* Hero Section - 简洁白色 */}
      <header className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-white border-b border-slate-100">
        <div className="max-w-3xl mx-auto text-center relative">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4 text-slate-800">
            {contentIndex.title}
          </h1>
          <p className="text-lg sm:text-xl text-slate-500 italic">
            {contentIndex.subtitle}
          </p>
          {/* 翻页按钮 - 跳转到 Workbook */}
          <a
            href="/#/workbook"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-0 p-2 text-slate-300 hover:text-indigo-600 transition-colors"
            title="查看更多文章"
          >
            <ChevronRight size={32} />
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-64 lg:flex-shrink-0">
            <div className="lg:sticky lg:top-20">
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
                {t('sidebar.tableOfContents')}
              </h2>
              <nav className="space-y-1">
                {articles.map((article) => (
                  <button
                    key={article.id}
                    onClick={() => scrollToArticle(article.id)}
                    className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                      activeArticle === article.id
                        ? 'bg-slate-800 text-white shadow-md'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <ChevronRight
                        size={14}
                        className={`transition-transform ${
                          activeArticle === article.id ? 'rotate-90' : ''
                        }`}
                      />
                      {article.title}
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          <div className="flex-1 min-w-0 bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
            {articles.map((article) => (
              <article key={article.id} id={article.id} className="mb-16 scroll-mt-24">
                <div className="max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {article.content}
                  </ReactMarkdown>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>

      {/* Download Section - 简洁风格 */}
      <section id="download" className="border-t border-slate-100 py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-semibold mb-8 text-slate-800">{t('download.title')}</h2>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a
              href="/downloads/teegal-setup.exe"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              {t('nav.teegalWorkspace')}
            </a>
          </div>
          
          {/* Teegal 介绍 - 信笺尾页风格 + 微信二维码 + 用户统计 */}
          <div className="flex flex-col gap-6 items-center">
            {/* 第一行：信笺介绍 + 微信二维码 */}
            <div className="flex flex-col md:flex-row gap-8 items-center justify-center">
              {/* 左侧：信笺介绍 */}
              <div className="text-center md:text-left text-slate-700 max-w-md">
                <div className="text-sm font-normal leading-relaxed space-y-3">
                  <p className="mb-3 text-slate-800 font-medium">{t('download.intro')}</p>
                  <p dangerouslySetInnerHTML={{ __html: t('download.featureAgent') }} />
                  <p dangerouslySetInnerHTML={{ __html: t('download.featureCompute') }} />
                </div>
              </div>
              {/* 右侧：微信二维码 */}
              <div className="flex flex-col items-center gap-2">
                <img
                  src="/images/wechat-qr.png"
                  alt="微信二维码"
                  className="w-32 h-32 rounded-lg border border-slate-300 object-cover"
                />
              </div>
            </div>
            {/* 第二行：用户统计展示 */}
            {stats && stats.totalRequests > 0 && (
              <div className="flex flex-col items-center justify-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-100 rounded-full text-indigo-700 text-sm">
                  <span>
                    今日来访 <strong className="font-semibold">{(stats.totalRequests + 1).toLocaleString()}</strong>
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm text-slate-400">
            {t('footer.copyright')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
